import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, ExternalLink } from 'lucide-react';

const Portfolio = () => {
  const navigate = useNavigate();

  // Prüfen ob der Benutzer authentifiziert ist
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('portfolioAuth');
    if (!isAuthenticated) {
      // Zurück zur Startseite, wenn nicht authentifiziert
      navigate('/');
    }
  }, [navigate]);

  const portfolioProjects = [
    {
      id: 'nordica',
      title: 'Nordica',
      category: 'Brand Development',
      description: 'Strategische Markenentwicklung und Corporate Identity für Nordica - ein umfassendes Rebranding-Projekt mit Fokus auf digitale Präsenz und Markenkommunikation.',
      tags: ['Branding', 'Digital Strategy', 'Corporate Identity'],
      color: 'from-lime-500 to-green-900',
      hasPage: true
    },
    {
      id: 'quickcook',
      title: 'QuickCook',
      category: 'Digital Brand Experience',
      description: 'Entwicklung einer innovativen digitalen Markenidentität für QuickCook - moderne Produktpräsentation, User Experience Design und Omnichannel-Strategie.',
      tags: ['UX/UI Design', 'Omnichannel', 'Brand Management'],
      color: 'from-fuchsia-600 to-red-600',
      hasPage: true
    },
    {
      id: 'phoenix',
      title: 'Phoenix',
      category: 'Brand Transformation',
      description: 'Komplette Markentransformation für Phoenix - von der Strategieentwicklung über die visuelle Gestaltung bis zur Implementierung über alle Touchpoints.',
      tags: ['Transformation', 'Strategy', 'Visual Identity'],
      color: 'from-amber-500 to-orange-800',
      hasPage: true
    },
    {
      id: 'presentations',
      title: 'Präsentationen',
      category: 'Business Communication',
      description: 'Professionelle Präsentationsdesigns für verschiedene Kunden - überzeugende Visualisierung komplexer Inhalte und wirkungsvolle Geschäftskommunikation.',
      tags: ['Presentation Design', 'Visual Communication', 'Storytelling'],
      color: 'from-cyan-500 to-blue-900',
      hasPage: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header with back button */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-primary-dark hover:text-accent-green"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zur Startseite
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="inline-block mb-4 px-4 py-2 glass-badge">
            <span className="text-accent-green font-semibold text-sm">✨ Brand Portfolio</span>
          </div>
          <h1 className="text-5xl font-bold text-primary-dark mb-6">
            Mein Portfolio
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ausgewählte Projekte im Bereich Brand Management, Strategieentwicklung und
            digitale Markenkommunikation
          </p>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-8">
            {portfolioProjects.map((project, index) => (
              <Card
                key={project.id}
                className="portfolio-card group cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                onClick={() => project.hasPage ? navigate(`/portfolio/${project.id}`) : null}
              >
                <div className={`h-48 bg-gradient-to-br ${project.color} rounded-t-xl relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all duration-300"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <h3 className="text-4xl font-bold text-white">{project.title}</h3>
                  </div>
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-accent-green uppercase tracking-wide">
                      {project.category}
                    </span>
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-accent-green transition-colors" />
                  </div>
                  <CardTitle className="text-2xl text-primary-dark">{project.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 mb-4 leading-relaxed">
                    {project.description}
                  </CardDescription>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-accent-green hover:text-white transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-primary-dark">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Interessiert an einer Zusammenarbeit?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Lassen Sie uns gemeinsam an Ihrem nächsten Brand-Projekt arbeiten.
          </p>
          <Button
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-accent-green hover:bg-accent-light text-white font-semibold px-8 py-3 text-lg"
          >
            Kontakt aufnehmen
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
